import { MedicineDetailResult } from 'features/graphql';

/**
 * 価格表の列幅計算ユーティリティ（D4 step4: PriceShowList から分離）。
 * いずれも純関数で、係数・計算結果は分離前と不変。
 */

// 卸名の長さに基づいて列幅を計算する関数
export const calculateWholesaleColumnWidth = (wholesaleName: string): number => {
  // 改行で分割して各行の幅を計算
  const lines = wholesaleName.split('\n');
  let maxCharWidth = 0;

  for (const line of lines) {
    let lineCharWidth = 0;
    for (const char of line) {
      // 日本語文字の判定
      if (
        char.match(
          /[\u3000-\u303f\u3040-\u309f\u30a0-\u30ff\uff00-\uff9f\u4e00-\u9faf\u3400-\u4dbf]/
        )
      ) {
        lineCharWidth += 1.8; // 日本語は実際の表示幅に近い1.8で計算
      } else {
        lineCharWidth += 0.6; // 英数字は0.6で計算
      }
    }
    maxCharWidth = Math.max(maxCharWidth, lineCharWidth);
  }

  // 文字幅を rem に変換（フォントサイズ0.9emを考慮）
  // 1rem = 16px基準で、0.9em ≈ 14.4px
  const nameWidth = maxCharWidth * 0.5; // 調整済みの係数

  // 価格表示は最大で "9,999,999.99" = 約5.5rem必要
  // 割引率は "99.99%" = 約3rem必要
  // padding左右で0.5rem×2 = 1rem必要
  const minContentWidth = 5.5; // rem (価格の実際の幅)

  // 卸名の幅とコンテンツの最小幅の大きい方を選択
  // padding分（1rem）を追加
  const calculatedWidth = Math.max(nameWidth, minContentWidth) + 1;

  // 最小幅を設定（短い名前でも最低限の幅を確保）
  return Math.max(calculatedWidth, 4); // 最小4rem
};

// fixed-col-4列（品名、規格、一般名、薬効分類、販売元名）の最適な幅を計算
export const calculateFixedCol4Width = (medicines: MedicineDetailResult[]): number => {
  let maxLength = 0;
  medicines.forEach((medicine) => {
    // 5つの項目すべてをチェック（品名・規格・一般名・薬効分類・販売元名）
    const textsToCheck = [
      medicine.medicineName,
      medicine.capacity,
      medicine.commonName,
      medicine.drugCategoryName,
      medicine.salesManufacturer,
    ];

    textsToCheck.forEach((text) => {
      if (!text) return;

      let textLength = 0;
      for (const char of text) {
        // 文字種別の判定
        if (
          // 全角文字（ひらがな、カタカナ、漢字、全角記号）
          char.match(/[\u3000-\u303f\u3040-\u309f\u30a0-\u30ff\u4e00-\u9faf\u3400-\u4dbf]/)
        ) {
          textLength += 1.0; // 全角文字は1.0文字分（等幅フォントに合わせて調整）
        } else if (
          // 半角カナ
          char.match(/[\uff61-\uff9f]/)
        ) {
          textLength += 0.5; // 半角カナは0.5文字分（半角として正確に）
        } else if (char === '(' || char === ')' || char === '｢' || char === '｣') {
          textLength += 0.4; // 括弧は0.4文字分
        } else if (char === '×' || char === ':' || char === '=' || char === '・') {
          textLength += 0.6; // 特殊記号は0.6文字分（少し広めに）
        } else if (char === ' ' || char === '　') {
          textLength += 0.3; // スペースは0.3文字分
        } else if (char.match(/[0-9]/)) {
          textLength += 0.55; // 数字は0.55文字分（少し広めに）
        } else if (char.match(/[mg]/)) {
          textLength += 0.5; // m,gは0.5文字分
        } else if (char.match(/[cL]/)) {
          textLength += 0.4; // c,Lは0.4文字分
        } else if (char.match(/[a-zA-Z]/)) {
          textLength += 0.45; // その他英字は0.45文字分
        } else {
          textLength += 0.5; // その他の文字は0.5文字分
        }
      }

      if (textLength > maxLength) {
        maxLength = textLength;
      }
    });
  });

  // 文字数から幅への変換
  // padding分（左右0.5rem×2 = 1rem）を追加
  const calculatedWidth = maxLength * 1.2 + 2;
  // 最小20rem、最大50remに制限（長い規格テキストに対応）
  return Math.max(20, Math.min(50, calculatedWidth));
};
